import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Search,
  Plus,
  Pencil,
  Trash2,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const categories = [
  { value: "contenants", label: "Contenants" },
  { value: "boissons", label: "Boissons" },
  { value: "emballages", label: "Emballages" },
  { value: "hygiene", label: "Hygiène" },
  { value: "personnalisation", label: "Personnalisation" },
];

const availabilityOptions = [
  { value: "en_stock", label: "En stock", icon: CheckCircle, color: "text-emerald-600 bg-emerald-50" },
  { value: "stock_faible", label: "Stock faible", icon: AlertTriangle, color: "text-amber-600 bg-amber-50" },
  { value: "rupture", label: "Rupture", icon: XCircle, color: "text-red-600 bg-red-50" },
];

interface Variant {
  id: string;
  product_id: string;
  size_name: string;
  dimensions: string | null;
  conditionnement: string | null;
  availability: string;
  display_order: number;
}

interface Product {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string | null;
  image_url: string | null;
  dimensions: string | null;
  availability: string;
  display_order: number;
  is_active: boolean;
  variants?: Variant[];
}

const emptyProduct = {
  name: "",
  slug: "",
  category: "contenants",
  description: "",
  image_url: "",
  dimensions: "",
  availability: "en_stock",
  display_order: 0,
  is_active: true,
};

const AdminProduits = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filtered, setFiltered] = useState<Product[]>([]);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editProduct, setEditProduct] = useState<any>(null);
  const [isNew, setIsNew] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
  const [editVariant, setEditVariant] = useState<any>(null);
  const [isNewVariant, setIsNewVariant] = useState(false);
  const [variantParentId, setVariantParentId] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    let result = products;
    if (categoryFilter !== "all") {
      result = result.filter((p) => p.category === categoryFilter);
    }
    if (search) {
      const s = search.toLowerCase();
      result = result.filter((p) => p.name.toLowerCase().includes(s));
    }
    setFiltered(result);
  }, [products, search, categoryFilter]);

  const fetchProducts = async () => {
    const { data: prods } = await supabase
      .from("products")
      .select("*")
      .order("display_order", { ascending: true });

    const { data: variants } = await supabase
      .from("product_variants")
      .select("*")
      .order("display_order", { ascending: true });

    if (prods) {
      const productsWithVariants = prods.map((p: any) => ({
        ...p,
        variants: (variants || []).filter((v: any) => v.product_id === p.id),
      }));
      setProducts(productsWithVariants);
    }
  };

  const generateSlug = (name: string) =>
    name.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const handleNew = () => {
    setIsNew(true);
    setEditProduct({ ...emptyProduct, display_order: products.length + 1 });
  };

  const handleSave = async () => {
    if (!editProduct.name || !editProduct.slug) {
      toast({ title: "Erreur", description: "Nom et slug requis.", variant: "destructive" });
      return;
    }
    const payload = {
      name: editProduct.name,
      slug: editProduct.slug,
      category: editProduct.category,
      description: editProduct.description || null,
      image_url: editProduct.image_url || null,
      dimensions: editProduct.dimensions || null,
      availability: editProduct.availability,
      display_order: editProduct.display_order,
      is_active: editProduct.is_active,
    };
    let error;
    if (isNew) {
      ({ error } = await supabase.from("products").insert(payload));
    } else {
      ({ error } = await supabase.from("products").update(payload).eq("id", editProduct.id));
    }
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: isNew ? "Produit ajouté" : "Produit mis à jour" });
    setEditProduct(null);
    setIsNew(false);
    fetchProducts();
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Supprimer "${name}" et toutes ses variantes ?`)) return;
    const { error } = await supabase.from("products").delete().eq("id", id);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Produit supprimé" });
    fetchProducts();
  };

  const toggleActive = async (id: string, current: boolean) => {
    await supabase.from("products").update({ is_active: !current }).eq("id", id);
    fetchProducts();
  };

  const updateVariantAvailability = async (id: string, availability: string) => {
    const { error } = await supabase.from("product_variants").update({ availability }).eq("id", id);
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Disponibilité mise à jour" });
    fetchProducts();
  };

  const handleNewVariant = (productId: string) => {
    setIsNewVariant(true);
    setVariantParentId(productId);
    setEditVariant({ size_name: "", dimensions: "", conditionnement: "", availability: "en_stock", display_order: 0 });
  };

  const handleSaveVariant = async () => {
    if (!editVariant.size_name) {
      toast({ title: "Erreur", description: "Le nom de la taille est requis.", variant: "destructive" });
      return;
    }
    const payload = {
      product_id: isNewVariant ? variantParentId : editVariant.product_id,
      size_name: editVariant.size_name,
      dimensions: editVariant.dimensions || null,
      conditionnement: editVariant.conditionnement || null,
      availability: editVariant.availability,
      display_order: editVariant.display_order,
    };
    let error;
    if (isNewVariant) {
      ({ error } = await supabase.from("product_variants").insert(payload));
    } else {
      ({ error } = await supabase.from("product_variants").update(payload).eq("id", editVariant.id));
    }
    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: isNewVariant ? "Taille ajoutée" : "Taille mise à jour" });
    setEditVariant(null);
    setIsNewVariant(false);
    setVariantParentId(null);
    fetchProducts();
  };

  const deleteVariant = async (id: string, name: string) => {
    if (!confirm(`Supprimer la taille "${name}" ?`)) return;
    await supabase.from("product_variants").delete().eq("id", id);
    toast({ title: "Taille supprimée" });
    fetchProducts();
  };

  const getAvailabilityBadge = (availability: string, small = false) => {
    const opt = availabilityOptions.find((o) => o.value === availability) || availabilityOptions[0];
    const Icon = opt.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${opt.color} ${small ? "text-[10px]" : "text-xs"}`}>
        <Icon className={small ? "w-3 h-3" : "w-3.5 h-3.5"} />
        {opt.label}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Gestion des produits</h2>
            <p className="text-sm text-muted-foreground">{products.length} produit(s) — Cliquez sur un produit pour gérer ses tailles</p>
          </div>
          <Button onClick={handleNew} className="gap-2">
            <Plus className="w-4 h-4" />
            Ajouter un produit
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="Rechercher..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button variant={categoryFilter === "all" ? "default" : "outline"} size="sm" onClick={() => setCategoryFilter("all")}>Tous</Button>
                {categories.map((cat) => (
                  <Button key={cat.value} variant={categoryFilter === cat.value ? "default" : "outline"} size="sm" onClick={() => setCategoryFilter(cat.value)}>
                    {cat.label}
                  </Button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Products list with expandable variants */}
        <div className="space-y-2">
          {filtered.map((p) => {
            const isExpanded = expandedProduct === p.id;
            return (
              <Card key={p.id} className={`border-0 shadow-sm overflow-hidden ${!p.is_active ? "opacity-50" : ""}`}>
                {/* Product row */}
                <div
                  className="flex items-center gap-4 p-4 cursor-pointer hover:bg-secondary/30 transition-colors"
                  onClick={() => setExpandedProduct(isExpanded ? null : p.id)}
                >
                  <button className="text-muted-foreground">
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{p.name}</p>
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-secondary text-secondary-foreground">
                        {categories.find((c) => c.value === p.category)?.label}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {(p.variants?.length || 0)} taille(s) · Ordre: {p.display_order}
                    </p>
                  </div>

                  <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => toggleActive(p.id, p.is_active)}
                      className={`p-1.5 rounded-lg transition-colors ${p.is_active ? "text-emerald-600 hover:bg-emerald-50" : "text-muted-foreground hover:bg-secondary"}`}
                      title={p.is_active ? "Visible" : "Masqué"}
                    >
                      {p.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <Button variant="ghost" size="sm" onClick={() => { setEditProduct(p); setIsNew(false); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id, p.name)} className="text-destructive hover:text-destructive">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Variants section */}
                {isExpanded && (
                  <div className="border-t border-border bg-secondary/20 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-muted-foreground">Tailles / Variantes</h4>
                      <Button size="sm" variant="outline" onClick={() => handleNewVariant(p.id)} className="gap-1 h-7 text-xs">
                        <Plus className="w-3 h-3" /> Ajouter une taille
                      </Button>
                    </div>

                    {(!p.variants || p.variants.length === 0) ? (
                      <p className="text-sm text-muted-foreground italic">Aucune taille définie.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {p.variants.map((v) => (
                          <div key={v.id} className="flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-secondary/50 transition-colors">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{v.size_name}</p>
                                {v.dimensions && <span className="text-xs text-muted-foreground">· {v.dimensions}</span>}
                              </div>
                              {v.conditionnement && <p className="text-xs text-muted-foreground">{v.conditionnement}</p>}
                            </div>

                            <Select value={v.availability} onValueChange={(val) => updateVariantAvailability(v.id, val)}>
                              <SelectTrigger className="w-[140px] h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {availabilityOptions.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    <span className="flex items-center gap-1">
                                      <opt.icon className={`w-3 h-3 ${opt.color.split(" ")[0]}`} />
                                      {opt.label}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditVariant(v); setIsNewVariant(false); }}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => deleteVariant(v.id, v.size_name)}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Edit Product Modal */}
        <Dialog open={!!editProduct} onOpenChange={() => { setEditProduct(null); setIsNew(false); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{isNew ? "Ajouter un produit" : "Modifier le produit"}</DialogTitle>
            </DialogHeader>
            {editProduct && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input value={editProduct.name} onChange={(e) => {
                    const name = e.target.value;
                    setEditProduct({ ...editProduct, name, ...(isNew ? { slug: generateSlug(name) } : {}) });
                  }} placeholder="Bols Kraft" />
                </div>
                <div className="space-y-2">
                  <Label>Slug (URL) *</Label>
                  <Input value={editProduct.slug} onChange={(e) => setEditProduct({ ...editProduct, slug: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Catégorie</Label>
                    <Select value={editProduct.category} onValueChange={(val) => setEditProduct({ ...editProduct, category: val })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ordre d'affichage</Label>
                    <Input type="number" value={editProduct.display_order} onChange={(e) => setEditProduct({ ...editProduct, display_order: parseInt(e.target.value) || 0 })} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea value={editProduct.description || ""} onChange={(e) => setEditProduct({ ...editProduct, description: e.target.value })} rows={3} />
                </div>
                <div className="space-y-2">
                  <Label>Visible sur le site</Label>
                  <div className="flex items-center gap-2 pt-1">
                    <button type="button" onClick={() => setEditProduct({ ...editProduct, is_active: !editProduct.is_active })}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editProduct.is_active ? "bg-primary" : "bg-muted"}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editProduct.is_active ? "translate-x-6" : "translate-x-1"}`} />
                    </button>
                    <span className="text-sm text-muted-foreground">{editProduct.is_active ? "Visible" : "Masqué"}</span>
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button onClick={handleSave} className="flex-1">{isNew ? "Ajouter" : "Enregistrer"}</Button>
                  <Button variant="outline" onClick={() => { setEditProduct(null); setIsNew(false); }}>Annuler</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Variant Modal */}
        <Dialog open={!!editVariant} onOpenChange={() => { setEditVariant(null); setIsNewVariant(false); setVariantParentId(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{isNewVariant ? "Ajouter une taille" : "Modifier la taille"}</DialogTitle>
            </DialogHeader>
            {editVariant && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom de la taille *</Label>
                  <Input value={editVariant.size_name} onChange={(e) => setEditVariant({ ...editVariant, size_name: e.target.value })} placeholder="ex: 500ml, Petite, FC0..." />
                </div>
                <div className="space-y-2">
                  <Label>Dimensions</Label>
                  <Input value={editVariant.dimensions || ""} onChange={(e) => setEditVariant({ ...editVariant, dimensions: e.target.value })} placeholder="ex: 160 x 50 x 30mm" />
                </div>
                <div className="space-y-2">
                  <Label>Conditionnement</Label>
                  <Input value={editVariant.conditionnement || ""} onChange={(e) => setEditVariant({ ...editVariant, conditionnement: e.target.value })} placeholder="ex: Carton de 1000 - Palette de 75" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Disponibilité</Label>
                    <Select value={editVariant.availability} onValueChange={(val) => setEditVariant({ ...editVariant, availability: val })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {availabilityOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ordre</Label>
                    <Input type="number" value={editVariant.display_order} onChange={(e) => setEditVariant({ ...editVariant, display_order: parseInt(e.target.value) || 0 })} />
                  </div>
                </div>
                <div className="flex gap-3 pt-4">
                  <Button onClick={handleSaveVariant} className="flex-1">{isNewVariant ? "Ajouter" : "Enregistrer"}</Button>
                  <Button variant="outline" onClick={() => { setEditVariant(null); setIsNewVariant(false); }}>Annuler</Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminProduits;
